import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api, esquecerTokenInstalacao } from './api';
import { AssistenteFlutuante } from './Assistente';
import { Aviso, AvisoDeVersao, BotaoTema, Logotipo } from './comum';
import { Aprovacoes } from './telas/Aprovacoes';
import { Assistente } from './telas/Assistente';
import { Chat } from './telas/Chat';
import { Configuracoes } from './telas/Configuracoes';
import { Inicio } from './telas/Inicio';
import { Integracoes } from './telas/Integracoes';
import { Inventario, type FiltroDeCartao } from './telas/Inventario';
import { Login } from './telas/Login';
import { Relatorios } from './telas/Relatorios';
import { Sentinela } from './telas/Sentinela';
import { Tickets } from './telas/Tickets';
import { Vulnerabilidades } from './telas/Vulnerabilidades';
import { Monitores } from './telas/Monitores';
import { Problemas } from './telas/Problemas';
import { Execucoes } from './telas/Execucoes';
import type { Acao, Capacidades, EstadoAuth, EstadoInstalacao, Usuario } from './tipos';

type Aba =
  | 'inicio'
  | 'inventario'
  | 'tickets'
  | 'chat'
  | 'aprovacoes'
  | 'sentinela'
  | 'monitores'
  | 'problemas'
  | 'execucoes'
  | 'vulnerabilidades'
  | 'relatorios'
  | 'integracoes'
  | 'configuracoes';

interface ItemMenu {
  readonly id: Aba;
  readonly rotulo: string;
  readonly exige: Acao;
  /** Símbolo do menu. Texto, não fonte de ícones: menos uma dependência. */
  readonly icone: string;
  /** Separador visual antes deste item. */
  readonly grupo?: string;
}

/**
 * 🔴 Os grupos do menu — vistos na tela, não no código.
 *
 * `grupo` marcava o início de um grupo e os itens seguintes herdavam. Só que a
 * lista estava intercalada — Governança, Governança, Inteligência, Operação —
 * e o cabeçalho era impresso a cada item que trazia o campo. Resultado na
 * barra lateral: **OPERAÇÃO, INTELIGÊNCIA e GOVERNANÇA aparecendo duas vezes
 * cada**, como se fossem seções diferentes com o mesmo nome.
 *
 * Agora o grupo é declarado em todo item e os itens de um grupo ficam juntos.
 * O cabeçalho sai só na virada — ver `grupoMudou` na renderização.
 *
 * ⚠️ Item novo vai ao lado dos irmãos, não no fim da lista: fora de ordem, o
 * cabeçalho do grupo dele volta a aparecer duas vezes.
 */
const ABAS: readonly ItemMenu[] = [
  { id: 'inicio', rotulo: 'Início', exige: 'ver_inventario', icone: '◳' },

  {
    id: 'inventario',
    rotulo: 'Inventário',
    exige: 'ver_inventario',
    icone: '▦',
    grupo: 'Operação',
  },
  { id: 'tickets', rotulo: 'Chamados', exige: 'ver_tickets', icone: '✉', grupo: 'Operação' },
  { id: 'execucoes', rotulo: 'Execuções', exige: 'ver_inventario', icone: '▶', grupo: 'Operação' },
  {
    id: 'vulnerabilidades',
    rotulo: 'Vulnerabilidades',
    // `ver_inventario` e não `ver_regras`: quem enxerga o parque enxerga o
    // risco dele. Exigir permissão de governança esconderia a tela justamente
    // de quem aplica patch.
    exige: 'ver_inventario',
    icone: '⚠',
    grupo: 'Operação',
  },
  {
    id: 'relatorios',
    rotulo: 'Relatórios',
    exige: 'ver_relatorios',
    icone: '▤',
    grupo: 'Operação',
  },

  { id: 'chat', rotulo: 'Perguntar', exige: 'perguntar_ia', icone: '✦', grupo: 'Inteligência' },
  {
    id: 'problemas',
    rotulo: 'Problemas',
    exige: 'ver_inventario',
    icone: '✧',
    grupo: 'Inteligência',
  },

  { id: 'aprovacoes', rotulo: 'Aprovações', exige: 'ver_tickets', icone: '✓', grupo: 'Governança' },
  { id: 'sentinela', rotulo: 'Sentinela', exige: 'ver_regras', icone: '◈', grupo: 'Governança' },
  {
    id: 'monitores',
    rotulo: 'Monitores',
    exige: 'ver_inventario',
    icone: '◌',
    grupo: 'Governança',
  },

  {
    id: 'integracoes',
    rotulo: 'Integrações',
    exige: 'ver_configuracao',
    icone: '⇄',
    grupo: 'Sistema',
  },
  {
    id: 'configuracoes',
    rotulo: 'Configurações',
    exige: 'ver_configuracao',
    icone: '⚙',
    grupo: 'Sistema',
  },
];

/**
 * Casca da aplicação.
 *
 * Decide entre três estados antes de desenhar qualquer coisa:
 * instalação incompleta → assistente; sem sessão → login; com sessão → produto.
 *
 * A ordem importa: perguntar pela instalação **antes** do login é o que evita
 * mostrar uma tela de entrada que ninguém consegue usar, porque sem conexão com
 * o KACE não há como autenticar ninguém.
 */
export function App(): ReactNode {
  const [carregando, setCarregando] = useState(true);
  const [instalacao, setInstalacao] = useState<EstadoInstalacao | null>(null);
  const [auth, setAuth] = useState<EstadoAuth | null>(null);
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [capacidades, setCapacidades] = useState<Capacidades>({});
  /**
   * Filtro que um cartão do painel mandou para o Inventário.
   *
   * Vive aqui porque atravessa duas abas. Some ao trocar de aba por conta
   * própria: filtro que sobrevive à navegação vira filtro fantasma — a pessoa
   * volta ao Inventário dias depois e vê uma lista curta sem entender por quê.
   */
  const [filtroDeCartao, setFiltroDeCartao] = useState<FiltroDeCartao | null>(null);
  const [aba, setAba] = useState<Aba>('inicio');
  const [avisoLogin, setAvisoLogin] = useState<string | null>(null);
  const [falhaBoot, setFalhaBoot] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const inst = await api.get<EstadoInstalacao>('/api/config/instalacao');
      setInstalacao(inst);

      const a = await api.get<EstadoAuth>('/api/auth/estado');
      setAuth(a);
      if (a.autenticado && a.usuario !== undefined) {
        setUsuario(a.usuario);
        setCapacidades(a.capacidades ?? {});
      }
    } catch (e: unknown) {
      setFalhaBoot(e instanceof Error ? e.message : String(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /** Sessão vencida em qualquer requisição derruba a tela inteira para o login. */
  const aoPerderSessao = useCallback(() => {
    setUsuario(null);
    setCapacidades({});
    setAvisoLogin('Sua sessão expirou.');
  }, []);

  const sair = useCallback(async () => {
    await api.post('/api/auth/sair').catch(() => undefined);
    esquecerTokenInstalacao();
    setUsuario(null);
    setCapacidades({});
    setAvisoLogin(null);
  }, []);

  if (carregando) {
    return (
      <div className="tela-cheia">
        <p className="suave">Carregando…</p>
      </div>
    );
  }

  if (falhaBoot !== null) {
    return (
      <div className="tela-cheia">
        <div className="caixa">
          <h1>Não foi possível iniciar</h1>
          <Aviso tipo="erro">{falhaBoot}</Aviso>
          <p className="suave">
            O serviço pode estar fora do ar ou ainda subindo. Recarregue a página em alguns
            segundos.
          </p>
        </div>
      </div>
    );
  }

  // Instalação incompleta: o assistente vem antes de tudo.
  if (instalacao !== null && !instalacao.utilizavel) {
    return (
      <Assistente
        estado={instalacao}
        aoConcluir={() => {
          void carregar();
        }}
      />
    );
  }

  if (usuario === null) {
    return (
      <Login
        organizacaoPadrao={auth?.organizacaoPadrao ?? ''}
        organizacoes={auth?.organizacoes ?? []}
        nomeDoCliente={instalacao?.nomeDoCliente ?? ''}
        avisoInicial={avisoLogin ?? (auth?.motivo === 'expirado' ? 'Sua sessão expirou.' : null)}
        acessoLocalDisponivel={auth?.acessoLocalDisponivel === true}
        aoEntrar={(u, c) => {
          setUsuario(u);
          setCapacidades(c);
          setAvisoLogin(null);
        }}
      />
    );
  }

  const visiveis = ABAS.filter((a) => capacidades[a.exige] === true);
  const atual = visiveis.some((a) => a.id === aba) ? aba : (visiveis[0]?.id ?? 'inicio');

  return (
    <div className="app">
      {/*
        Navegação lateral.

        Foi para a esquerda quando o produto passou de cinco para oito áreas: em
        abas horizontais, "Configurações" já estava perto do fim da linha e a
        próxima área começaria a rolar. Vertical cresce sem competir por
        espaço, e sobra largura para tabela — que é o que estas telas mostram.
      */}
      <aside className="lateral">
        <div className="lateral-topo">
          <Logotipo tamanho={17} />
          <span className="cliente-lateral">{instalacao?.nomeDoCliente || 'Plataforma KACE'}</span>
        </div>

        <nav>
          {visiveis.map((a, i) => {
            /*
              O cabeçalho sai na VIRADA de grupo, comparando com o item visível
              anterior — não com o anterior da lista. Sem isso, esconder um item
              por permissão deixaria o grupo dele órfão de título (ou com título
              e nenhum item embaixo).
            */
            const grupoMudou = a.grupo !== undefined && a.grupo !== visiveis[i - 1]?.grupo;
            return (
              <div key={a.id}>
                {grupoMudou && <p className="grupo-menu">{a.grupo}</p>}
                <button
                  onClick={() => {
                    // Navegar pela barra lateral limpa o filtro do cartão: quem
                    // clicou em "Inventário" quer o inventário, não o recorte de
                    // um cartão que abriu antes.
                    setFiltroDeCartao(null);
                    setAba(a.id);
                  }}
                  className={a.id === atual ? 'ativo' : ''}
                  {...(a.id === atual ? { 'aria-current': 'page' as const } : {})}
                >
                  <span className="icone-menu" aria-hidden="true">
                    {a.icone}
                  </span>
                  {a.rotulo}
                </button>
              </div>
            );
          })}
        </nav>

        <div className="lateral-rodape">
          <div className="quem">
            <strong>{usuario.nome}</strong>
            <span>{usuario.orgNome}</span>
          </div>
          <div className="acoes-lateral">
            <BotaoTema />
            <button onClick={() => void sair()}>Sair</button>
          </div>
        </div>
      </aside>

      <main>
        <AvisoDeVersao />
        {atual === 'inicio' && (
          <Inicio
            aoPerderSessao={aoPerderSessao}
            irPara={(destino) => setAba(destino)}
            podeSincronizar={capacidades.sincronizar === true}
            meuLogin={usuario.login}
            aoAbrirNoInventario={(f) => {
              setFiltroDeCartao(f);
              setAba('inventario');
            }}
          />
        )}
        {atual === 'inventario' && (
          <Inventario
            aoPerderSessao={aoPerderSessao}
            podeSincronizar={capacidades.sincronizar === true}
            podeAbrirChamado={capacidades.decidir_sugestao === true}
            filtroDeCartao={filtroDeCartao}
            aoLimparFiltro={() => setFiltroDeCartao(null)}
          />
        )}
        {atual === 'tickets' && (
          <Tickets aoPerderSessao={aoPerderSessao} podeTriar={capacidades.triar_ticket === true} />
        )}
        {atual === 'chat' && <Chat aoPerderSessao={aoPerderSessao} />}
        {atual === 'aprovacoes' && (
          <Aprovacoes
            aoPerderSessao={aoPerderSessao}
            podeDecidir={capacidades.decidir_sugestao === true}
          />
        )}
        {atual === 'vulnerabilidades' && <Vulnerabilidades aoPerderSessao={aoPerderSessao} />}
        {atual === 'sentinela' && (
          <Sentinela
            aoPerderSessao={aoPerderSessao}
            /*
             * 🔴 Quatro permissões distintas, não uma.
             *
             * Até aqui a tela usava `aprovar_remediacao` para tudo — inclusive
             * "Nova regra" e "Editar". Como aprovar exige `scripts: WRITE` no
             * KACE, quem administra o parque sem executar script (perfil
             * legítimo e comum) não conseguia sequer criar uma regra.
             */
            podeAprovar={capacidades.aprovar_remediacao === true}
            podeDispensar={capacidades.dispensar_violacao === true}
            podeEditarRegra={capacidades.editar_regra === true}
            podeRegraComRemediacao={capacidades.editar_regra_com_remediacao === true}
          />
        )}
        {atual === 'monitores' && (
          <Monitores
            aoPerderSessao={aoPerderSessao}
            // Criar e ativar monitor define o que o produto vigia no parque —
            // o servidor exige `ver_inventario`, e a tela esconde o que daria 403.
            podeCriar={capacidades.ver_inventario === true}
          />
        )}
        {atual === 'problemas' && <Problemas aoPerderSessao={aoPerderSessao} />}
        {atual === 'execucoes' && (
          <Execucoes
            aoPerderSessao={aoPerderSessao}
            // Disparar sync e cancelar job é a mesma autoridade que sincronizar
            // à mão — o servidor confere `sincronizar` na rota.
            podeDisparar={capacidades.sincronizar === true}
          />
        )}
        {atual === 'relatorios' && (
          <Relatorios
            aoPerderSessao={aoPerderSessao}
            podeExportar={capacidades.exportar_benchmark === true}
          />
        )}
        {atual === 'integracoes' && (
          <Integracoes
            aoPerderSessao={aoPerderSessao}
            podeEditar={capacidades.editar_configuracao === true}
          />
        )}
        {atual === 'configuracoes' && (
          <Configuracoes
            aoPerderSessao={aoPerderSessao}
            podeEditar={capacidades.editar_configuracao === true}
            // 🔴 Homologar script não é "editar configuração". Quem autoriza o
            // produto a rodar código na máquina do cliente precisa da mesma
            // permissão KACE que rodaria o script à mão — `aprovar_remediacao`
            // exige `scripts: WRITE`, e é ela que o servidor confere.
            podeHomologarScript={capacidades.aprovar_remediacao === true}
          />
        )}
      </main>

      {/* Fica em todas as telas — é o ponto de ajuda que o KACE não tem. */}
      <AssistenteFlutuante
        aoPerderSessao={aoPerderSessao}
        irPara={(destino) => setAba(destino as Aba)}
      />
    </div>
  );
}
